import React, { useEffect, useMemo, useRef, useState } from 'react'
import html2canvas from 'html2canvas'
import { fetchSalesSummary } from '../services/salesSummaryClient'
import { useStrategicCountdownData } from '../hooks/useStrategicCountdownData'

const START_OF_YEAR_CLOSEABLE_PLUG = 139

const PROJECT_LIST = ['Aria', 'Fusion', 'SoMi Condos', 'SoMi Towns', 'Vida']
const LEGEND_ROWS = [
  { label: 'input', fill: '#cfe2f3' },
  { label: 'minor calc', fill: '#fff2cc' },
  { label: 'major calc', fill: '#ffdccc' }
]
const HBFA_LOGO_SRC = '/assets/hbfa-logo.png'
const AGENDA_CAPTURE_WIDTH = 820
const AGENDA_BORDER_COLOR = '#666666'
const STRATEGIC_TITLE_ROW_HEIGHT = 24
const STRATEGIC_HEADER_ROW_HEIGHT = 48
const STRATEGIC_BODY_ROW_HEIGHT = 34

function waitForCaptureRender() {
  return new Promise(resolve => {
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  })
}

function getPreviousSunday(baseDate = new Date()) {
  const date = new Date(baseDate)
  date.setHours(0, 0, 0, 0)
  const day = date.getDay()
  date.setDate(date.getDate() - day)
  return date
}

function getNov15(currentYear) {
  return new Date(currentYear, 10, 15)
}

function differenceInDays(startDate, endDate) {
  const start = Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
  const end = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
  return (end - start) / (1000 * 60 * 60 * 24)
}

function formatAgendaDate(value) {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const month = date.getMonth() + 1
  const day = date.getDate()
  const year = String(date.getFullYear()).slice(-2)
  return `${month}/${day}/${year}`
}

function formatIsoDate(value) {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

function formatDecimal(value) {
  if (!Number.isFinite(value)) return '--'
  return value.toFixed(2)
}

function formatWhole(value) {
  if (!Number.isFinite(value)) return '--'
  return Math.round(value).toString()
}

function formatParenWhole(value) {
  if (!Number.isFinite(value)) return '--'
  const rounded = Math.round(value)
  return rounded < 0 ? `(${Math.abs(rounded)})` : `${rounded}`
}

function buildPdfFromJpeg({ dataUrl, imageWidth, imageHeight, pageWidth = 612, pageHeight = 792, margin = 18 }) {
  const base64 = dataUrl.split(',')[1] || ''
  const binary = window.atob(base64)
  const imageBytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    imageBytes[index] = binary.charCodeAt(index)
  }

  const availableWidth = pageWidth - margin * 2
  const availableHeight = pageHeight - margin * 2
  const scale = Math.min(availableWidth / imageWidth, availableHeight / imageHeight)
  const drawWidth = imageWidth * scale
  const drawHeight = imageHeight * scale
  const drawX = (pageWidth - drawWidth) / 2
  const drawY = (pageHeight - drawHeight) / 2

  const content = `q\n${drawWidth.toFixed(2)} 0 0 ${drawHeight.toFixed(2)} ${drawX.toFixed(2)} ${drawY.toFixed(2)} cm\n/Im0 Do\nQ`

  const encoder = new TextEncoder()
  const parts = [encoder.encode('%PDF-1.4\n')]
  const offsets = [0]
  let position = parts[0].length

  function pushChunk(chunk) {
    parts.push(chunk)
    position += chunk.length
  }

  function pushText(text) {
    pushChunk(encoder.encode(text))
  }

  function addObject(objectNumber, chunks) {
    offsets[objectNumber] = position
    pushText(`${objectNumber} 0 obj\n`)
    chunks.forEach(chunk => pushChunk(typeof chunk === 'string' ? encoder.encode(chunk) : chunk))
    pushText('\nendobj\n')
  }

  addObject(1, ['<< /Type /Catalog /Pages 2 0 R >>'])
  addObject(2, ['<< /Type /Pages /Count 1 /Kids [3 0 R] >>'])
  addObject(3, [
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>`
  ])
  addObject(4, [
    `<< /Type /XObject /Subtype /Image /Width ${imageWidth} /Height ${imageHeight} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${imageBytes.length} >>\nstream\n`,
    imageBytes,
    '\nendstream'
  ])
  const contentBytes = encoder.encode(content)
  addObject(5, [
    `<< /Length ${contentBytes.length} >>\nstream\n`,
    contentBytes,
    '\nendstream'
  ])

  const xrefOffset = position
  pushText(`xref\n0 6\n0000000000 65535 f \n`)
  for (let objectNumber = 1; objectNumber <= 5; objectNumber += 1) {
    pushText(`${String(offsets[objectNumber]).padStart(10, '0')} 00000 n \n`)
  }
  pushText(`trailer << /Size 6 /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`)

  return new Blob(parts, { type: 'application/pdf' })
}

function agendaCellStyle({
  bold = false,
  center = false,
  right = false,
  fill = '#ffffff',
  fontSize = 13,
  borderColor = AGENDA_BORDER_COLOR,
  color = '#000000',
  height
} = {}) {
  return {
    border: `0.4px solid ${borderColor}`,
    backgroundColor: fill,
    color,
    fontFamily: 'Arial, sans-serif',
    fontSize,
    fontWeight: bold ? 700 : 400,
    padding: '3px 5px',
    textAlign: center ? 'center' : (right ? 'right' : 'left'),
    verticalAlign: 'middle',
    lineHeight: 1.15,
    height
  }
}

function StrategicAgendaLabel({ row }) {
  const [projectLabel, segmentLabel = ''] = String(row.agendaLabel || '').split(' - ')
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.05 }}>
      <span>{projectLabel}</span>
      <span>{segmentLabel}</span>
    </span>
  )
}

function AgendaSheet({ currentYear, closingsYtd, closingsByRow, rows, totalRow }) {
  const priorSunday = useMemo(() => getPreviousSunday(), [])
  const finalSaleDate = useMemo(() => getNov15(currentYear), [currentYear])

  const derived = useMemo(() => {
    const monthsRemaining = differenceInDays(priorSunday, finalSaleDate) / 30
    const weeksRemaining = differenceInDays(priorSunday, finalSaleDate) / 7
    const remainingCloseable = totalRow.total
    const backlog = totalRow.backlog
    const inventoryAndModels = totalRow.inventory + totalRow.models
    const closingsCommitment = totalRow.plan
    const remainingCommitment = closingsCommitment - closingsYtd
    const neededNewSales = remainingCommitment - backlog
    const somiBmrLoss = rows
      .filter(row => row.segment === 'bmr' && (row.projectId === 'SoMi A' || row.projectId === 'SoMi B'))
      .reduce((sum, row) => sum + row.total, 0)
    const ariaLoss = rows.find(row => row.projectId === 'Aria' && row.segment === 'market')?.total ?? 0
    const fusionLoss = rows
      .filter(row => row.projectId === 'Fusion')
      .reduce((sum, row) => sum + row.total, 0)
    const worstCaseRemaining = START_OF_YEAR_CLOSEABLE_PLUG - somiBmrLoss - ariaLoss - fusionLoss

    return {
      priorSunday,
      finalSaleDate,
      monthsRemaining,
      weeksRemaining,
      inventoryAndModels,
      backlog,
      remainingCloseable,
      closingsCommitment,
      remainingCommitment,
      salesNeededPerMonth: monthsRemaining ? neededNewSales / monthsRemaining : 0,
      salesNeededPerWeek: weeksRemaining ? neededNewSales / weeksRemaining : 0,
      remainingCushion: remainingCloseable - closingsCommitment,
      somiBmrLoss,
      ariaLoss,
      fusionLoss,
      worstCaseRemaining,
      worstCaseCushion: worstCaseRemaining - remainingCommitment
    }
  }, [closingsYtd, currentYear, finalSaleDate, priorSunday, rows, totalRow])

  const orderedRows = useMemo(() => rows.map(row => ({
    ...row,
    workbookFill: row.fill
  })), [rows])

  return (
    <div
      style={{
        width: AGENDA_CAPTURE_WIDTH,
        backgroundColor: '#ffffff',
        color: '#000000',
        padding: '12px 12px 14px',
        fontFamily: 'Arial, sans-serif'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 12
        }}
      >
        <img
          src={HBFA_LOGO_SRC}
          alt="HBFA logo"
          style={{ width: 124, height: 'auto', objectFit: 'contain', flexShrink: 0 }}
        />
        <div style={{ fontWeight: 700, fontSize: 22, letterSpacing: 0.3 }}>
          Northern California Division
        </div>
      </div>
      <div style={{
        textAlign: 'center',
        fontWeight: 700,
        fontSize: 24,
        marginBottom: 8,
        backgroundColor: '#000000',
        color: '#ffffff',
        padding: '6px 10px',
        marginLeft: -12,
        marginRight: -12
      }}>
        AGENDA - the operations staff meeting
      </div>
      <div style={{ textAlign: 'center', fontStyle: 'italic', fontSize: 18, marginBottom: 14 }}>
        "what is preventing us from reaching our goals for {currentYear}?"
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <tbody>
            <tr>
              <td style={agendaCellStyle({ bold: true, center: true, fill: '#cfe2f3' })}>{formatAgendaDate(derived.priorSunday)}</td>
              <td style={agendaCellStyle()}>- through this date (Sunday Night)</td>
            </tr>
            <tr>
              <td style={agendaCellStyle({ bold: true, center: true, fill: '#cfe2f3' })}>{formatAgendaDate(derived.finalSaleDate)}</td>
              <td style={agendaCellStyle()}>- last sale date for CLOSE this year</td>
            </tr>
            <tr>
              <td style={agendaCellStyle({ bold: true, center: true, fill: '#fff2cc' })}>{formatDecimal(derived.monthsRemaining)}</td>
              <td style={agendaCellStyle()}>- sales months remaining</td>
            </tr>
            <tr>
              <td style={agendaCellStyle({ bold: true, center: true, fill: '#fff2cc' })}>{formatDecimal(derived.weeksRemaining)}</td>
              <td style={agendaCellStyle()}>- sales weeks remaining</td>
            </tr>
            <tr>
              <td style={agendaCellStyle({ bold: true, center: true, fill: '#fff2cc' })}>{formatDecimal(derived.salesNeededPerMonth)}</td>
              <td style={agendaCellStyle()}>- sales needed per month</td>
            </tr>
            <tr>
              <td style={agendaCellStyle({ bold: true, center: true, fill: '#fff2cc' })}>{formatDecimal(derived.salesNeededPerWeek)}</td>
              <td style={agendaCellStyle()}>- sales needed per week</td>
            </tr>
          </tbody>
        </table>

        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <tbody>
            <tr>
              <td style={agendaCellStyle({ right: true })}>inventory and models -</td>
              <td style={agendaCellStyle({ bold: true, center: true, fill: '#fff2cc' })}>{formatWhole(derived.inventoryAndModels)}</td>
            </tr>
            <tr>
              <td style={agendaCellStyle({ center: true, bold: true })}>+</td>
              <td style={agendaCellStyle()} />
            </tr>
            <tr>
              <td style={agendaCellStyle({ right: true })}>backlog -</td>
              <td style={agendaCellStyle({ bold: true, center: true, fill: '#fff2cc' })}>{formatWhole(derived.backlog)}</td>
            </tr>
            <tr>
              <td style={agendaCellStyle({ center: true, bold: true })}>=</td>
              <td style={agendaCellStyle()} />
            </tr>
            <tr>
              <td style={agendaCellStyle({ right: true })}>remaining "CLOSEABLE" homes -</td>
              <td style={agendaCellStyle({ bold: true, center: true, fill: '#ffdccc' })}>{formatWhole(derived.remainingCloseable)}</td>
            </tr>
            <tr>
              <td style={agendaCellStyle({ right: true })}>business plan CLOSINGS commitment -</td>
              <td style={agendaCellStyle({ bold: true, center: true, fill: '#cfe2f3' })}>{formatWhole(derived.closingsCommitment)}</td>
            </tr>
            <tr>
              <td style={agendaCellStyle({ right: true })}>CLOSED to date -</td>
              <td style={agendaCellStyle({ bold: true, center: true, fill: '#cfe2f3' })}>{formatWhole(closingsYtd)}</td>
            </tr>
            <tr>
              <td style={agendaCellStyle({ center: true, bold: true })}>=</td>
              <td style={agendaCellStyle()} />
            </tr>
            <tr>
              <td style={agendaCellStyle({ right: true })}>remaining business plan commitment homes to CLOSE -</td>
              <td style={agendaCellStyle({ bold: true, center: true, fill: '#ffdccc' })}>{formatWhole(derived.remainingCommitment)}</td>
            </tr>
            <tr>
              <td style={agendaCellStyle({ right: true })}>remaining "cushion" -</td>
              <td style={agendaCellStyle({ bold: true, center: true, fill: '#ffdccc' })}>{formatWhole(derived.remainingCushion)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>{currentYear} STRATEGIC COUNTDOWN</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.72fr 1.18fr', gap: 10, marginBottom: 12 }}>
        <div>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th colSpan="5" style={agendaCellStyle({ bold: true, fill: '#f3f3f3', center: true, height: STRATEGIC_TITLE_ROW_HEIGHT })}>"Universe" available to CLOSE</th>
              </tr>
              <tr>
                <th style={agendaCellStyle({ bold: true, fill: '#f3f3f3', fontSize: 12, height: STRATEGIC_HEADER_ROW_HEIGHT })}>project/product</th>
                <th style={agendaCellStyle({ bold: true, center: true, fill: '#f3f3f3', fontSize: 12, height: STRATEGIC_HEADER_ROW_HEIGHT })}>inventory universe</th>
                <th style={agendaCellStyle({ bold: true, center: true, fill: '#f3f3f3', fontSize: 12, height: STRATEGIC_HEADER_ROW_HEIGHT })}>backlog ("sold") universe</th>
                <th style={agendaCellStyle({ bold: true, center: true, fill: '#f3f3f3', fontSize: 12, height: STRATEGIC_HEADER_ROW_HEIGHT })}>model universe</th>
                <th style={agendaCellStyle({ bold: true, center: true, fill: '#f3f3f3', fontSize: 12, height: STRATEGIC_HEADER_ROW_HEIGHT })}>REMAINING {currentYear} universe</th>
              </tr>
            </thead>
            <tbody>
              {orderedRows.map(row => (
                <tr key={row.key}>
                  <td style={agendaCellStyle({ bold: true, fill: row.workbookFill, fontSize: 12, color: row.ink, height: STRATEGIC_BODY_ROW_HEIGHT })}><StrategicAgendaLabel row={row} /></td>
                  <td style={agendaCellStyle({ center: true, fontSize: 12, height: STRATEGIC_BODY_ROW_HEIGHT })}>{formatWhole(row.inventory)}</td>
                  <td style={agendaCellStyle({ center: true, fontSize: 12, height: STRATEGIC_BODY_ROW_HEIGHT })}>{formatWhole(row.backlog)}</td>
                  <td style={agendaCellStyle({ center: true, fontSize: 12, height: STRATEGIC_BODY_ROW_HEIGHT })}>{formatWhole(row.models)}</td>
                  <td style={agendaCellStyle({ center: true, fontSize: 12, bold: true, fill: '#fff2cc', height: STRATEGIC_BODY_ROW_HEIGHT })}>{formatWhole(row.total)}</td>
                </tr>
              ))}
              <tr>
                <td style={agendaCellStyle({ bold: true, fill: '#f3f3f3', height: STRATEGIC_BODY_ROW_HEIGHT })}>TOTALS</td>
                <td style={agendaCellStyle({ center: true, bold: true, fill: '#fff2cc', height: STRATEGIC_BODY_ROW_HEIGHT })}>{formatWhole(totalRow.inventory)}</td>
                <td style={agendaCellStyle({ center: true, bold: true, fill: '#ffdccc', height: STRATEGIC_BODY_ROW_HEIGHT })}>{formatWhole(totalRow.backlog)}</td>
                <td style={agendaCellStyle({ center: true, bold: true, fill: '#fff2cc', height: STRATEGIC_BODY_ROW_HEIGHT })}>{formatWhole(totalRow.models)}</td>
                <td style={agendaCellStyle({ center: true, bold: true, fill: '#ffdccc', height: STRATEGIC_BODY_ROW_HEIGHT })}>{formatWhole(totalRow.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th colSpan="3" style={agendaCellStyle({ bold: true, fill: '#f3f3f3', center: true, height: STRATEGIC_TITLE_ROW_HEIGHT })}>CLOSINGS</th>
              </tr>
              <tr>
                <th style={agendaCellStyle({ bold: true, fill: '#f3f3f3', fontSize: 12, height: STRATEGIC_HEADER_ROW_HEIGHT })}>project/product</th>
                <th style={agendaCellStyle({ bold: true, center: true, fill: '#f3f3f3', fontSize: 12, height: STRATEGIC_HEADER_ROW_HEIGHT })}>Closings</th>
                <th style={agendaCellStyle({ bold: true, center: true, fill: '#f3f3f3', fontSize: 12, height: STRATEGIC_HEADER_ROW_HEIGHT })}>Business Plan</th>
              </tr>
            </thead>
            <tbody>
              {orderedRows.map(row => (
                <tr key={`${row.key}-plan`}>
                  <td style={agendaCellStyle({ bold: true, fill: row.workbookFill, fontSize: 12, color: row.ink, height: STRATEGIC_BODY_ROW_HEIGHT })}><StrategicAgendaLabel row={row} /></td>
                  <td style={agendaCellStyle({ center: true, fontSize: 12, height: STRATEGIC_BODY_ROW_HEIGHT })}>{formatWhole(closingsByRow[row.key] ?? 0)}</td>
                  <td style={agendaCellStyle({ center: true, fontSize: 12, height: STRATEGIC_BODY_ROW_HEIGHT })}>{formatWhole(row.plan)}</td>
                </tr>
              ))}
              <tr>
                <td style={agendaCellStyle({ bold: true, fill: '#f3f3f3', height: STRATEGIC_BODY_ROW_HEIGHT })}>TOTAL</td>
                <td style={agendaCellStyle({ center: true, bold: true, fill: '#ffdccc', height: STRATEGIC_BODY_ROW_HEIGHT })}>{formatWhole(closingsYtd)}</td>
                <td style={agendaCellStyle({ center: true, bold: true, fill: '#ffdccc', height: STRATEGIC_BODY_ROW_HEIGHT })}>{formatWhole(totalRow.plan)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.7fr 1.25fr', gap: 10 }}>
        <div>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th colSpan="4" style={agendaCellStyle({ bold: true, fill: '#f3f3f3', center: true })}>PROJECTS</th>
              </tr>
            </thead>
            <tbody>
              {PROJECT_LIST.map(project => (
                <tr key={project}>
                  <td style={agendaCellStyle({ fontSize: 12 })}>{project}</td>
                  <td style={agendaCellStyle({ fontSize: 12 })} />
                  <td style={agendaCellStyle({ fontSize: 12 })} />
                  <td style={agendaCellStyle({ fontSize: 12 })} />
                </tr>
              ))}
              <tr>
                <td colSpan="4" style={agendaCellStyle({ bold: true, fill: '#f3f3f3', center: true })}>LEGEND</td>
              </tr>
              <tr>
                <td style={agendaCellStyle({ fontSize: 12 })} />
                {LEGEND_ROWS.map(row => (
                  <td key={row.label} style={agendaCellStyle({ fontSize: 12 })}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 16,
                        height: 12,
                        border: `0.4px solid ${AGENDA_BORDER_COLOR}`,
                        backgroundColor: row.fill,
                        marginRight: 8,
                        verticalAlign: 'middle'
                      }}
                    />
                    {row.label}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <div>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th colSpan="2" style={agendaCellStyle({ bold: true, fill: '#f3f3f3', center: true })}>"WORST CASE" CUSHION ANALYSIS</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={agendaCellStyle()}>remaining "closeable":</td>
                <td style={agendaCellStyle({ center: true, fill: '#cfe2f3' })}>{formatWhole(START_OF_YEAR_CLOSEABLE_PLUG)}</td>
              </tr>
              <tr>
                <td style={agendaCellStyle()}>lose SOMI BMRs:</td>
                <td style={agendaCellStyle({ center: true })}>{formatParenWhole(-derived.somiBmrLoss)}</td>
              </tr>
              <tr>
                <td style={agendaCellStyle()}>lose all ARIA production:</td>
                <td style={agendaCellStyle({ center: true })}>{formatParenWhole(-derived.ariaLoss)}</td>
              </tr>
              <tr>
                <td style={agendaCellStyle()}>lose all FUSION inventory:</td>
                <td style={agendaCellStyle({ center: true })}>{formatParenWhole(-derived.fusionLoss)}</td>
              </tr>
              <tr>
                <td style={agendaCellStyle()} />
                <td style={agendaCellStyle({ center: true, fill: '#ffdccc' })}>{formatWhole(derived.worstCaseRemaining)}</td>
              </tr>
              <tr>
                <td style={agendaCellStyle()}>remaining commitment:</td>
                <td style={agendaCellStyle({ center: true })}>{formatParenWhole(-derived.remainingCommitment)}</td>
              </tr>
              <tr>
                <td style={agendaCellStyle({ bold: true })}>"worst case" cushion:</td>
                <td style={agendaCellStyle({ center: true, fill: '#ffdccc', bold: true })}>{formatWhole(derived.worstCaseCushion)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default function OperationsAgendaReport() {
  const reportRef = useRef(null)
  const { currentYear, error: countdownError, loading: countdownLoading, rows, totalRow } = useStrategicCountdownData()
  const [salesSummary, setSalesSummary] = useState(null)
  const [salesSummaryLoading, setSalesSummaryLoading] = useState(false)
  const [salesSummaryError, setSalesSummaryError] = useState('')
  const [status, setStatus] = useState('')
  const [generating, setGenerating] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function loadSalesSummary() {
      setSalesSummaryLoading(true)
      setSalesSummaryError('')
      try {
        const result = await fetchSalesSummary()
        if (!cancelled) {
          setSalesSummary(result)
        }
      } catch (err) {
        if (!cancelled) {
          setSalesSummaryError(err.message || 'Failed to load sales summary')
        }
      } finally {
        if (!cancelled) {
          setSalesSummaryLoading(false)
        }
      }
    }

    loadSalesSummary()
    return () => {
      cancelled = true
    }
  }, [])

  const closingsYtd = salesSummary?.closings_ytd ?? 0
  const closingsByRow = salesSummary?.closings_ytd_by_row ?? {}
  const reportReady = !countdownLoading && !salesSummaryLoading && !countdownError && !salesSummaryError && rows.length > 0
  const priorSunday = useMemo(() => getPreviousSunday(), [])

  const handleDownloadPdf = async () => {
    setStatus('')
    if (!reportReady || !reportRef.current) {
      setStatus('Agenda data is still loading.')
      return
    }

    try {
      setGenerating(true)
      await waitForCaptureRender()
      const canvas = await html2canvas(reportRef.current, {
        backgroundColor: '#ffffff',
        scale: 2
      })
      const pdfBlob = buildPdfFromJpeg({
        dataUrl: canvas.toDataURL('image/jpeg', 0.94),
        imageWidth: canvas.width,
        imageHeight: canvas.height
      })
      const url = window.URL.createObjectURL(pdfBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `operations-agenda-${formatIsoDate(priorSunday)}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      setStatus('Operations agenda PDF generated.')
    } catch (err) {
      setStatus(err.message || 'Failed to generate operations agenda PDF.')
    } finally {
      setGenerating(false)
    }
  }

  return (
    <>
      <p>
        Generates the workbook-style weekly operations agenda from the live strategic countdown
        and current-year closings data.
      </p>

      <div style={{ marginBottom: 16 }}>
        <h4>Current Inputs:</h4>
        <ul>
          <li>Prior Sunday cutoff date for the current week</li>
          <li>November 15 of the current year as the closing target date</li>
          <li>Dashboard strategic countdown inventory, backlog, and model universe counts</li>
          <li>Current-year closings YTD from the sales summary endpoint</li>
          <li>Fixed start-of-year closeable plug of 139 for the worst-case cushion block</li>
        </ul>
      </div>

      {(countdownError || salesSummaryError) && (
        <div className="banner warn">
          <p>Error: {countdownError || salesSummaryError}</p>
        </div>
      )}

      {status && (
        <div className="banner info">
          <p>{status}</p>
        </div>
      )}

      <div className="actions">
        <button
          type="button"
          onClick={handleDownloadPdf}
          disabled={generating || !reportReady}
          className="btn primary"
        >
          {generating ? 'Generating Agenda PDF...' : 'Generate Operations Agenda PDF'}
        </button>
      </div>

      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '-20000px',
          top: 0,
          width: AGENDA_CAPTURE_WIDTH,
          pointerEvents: 'none'
        }}
      >
        <div ref={reportRef}>
          <AgendaSheet
            currentYear={currentYear}
            closingsYtd={closingsYtd}
            closingsByRow={closingsByRow}
            rows={rows}
            totalRow={totalRow}
          />
        </div>
      </div>
    </>
  )
}
